/**
 * Gateway Registry
 *
 * Single source of truth for all payment method kind configuration.
 * Consolidates UI mappings (icons, text keys, CSS modifiers, rendering order),
 * add behavior, device support, and PAYG visibility that were previously
 * scattered across multiple switch/case functions in different files.
 *
 * When adding a new payment method:
 * 1. Add the kind to `PaymentMethodKind` in types/shared.ts
 * 2. Add one entry to `PAYMENT_KIND_REGISTRY` below
 * 3. Add one entry to `GATEWAY_PREFIX_REGISTRY` in gateways/componentSelector.tsx
 * 4. Create the Charge component file
 */

import type { PaymentMethodKind } from '../types';
import type { AnalyticsContextValue } from '../types/analytics';
import type { OnLogEvent } from '../types/logger';
import { PURCHASE_FLOW_ICONS, PURCHASE_FLOW_TEXT_KEYS } from '../utils/constants';
import { applePayIsSupported, googlePayIsSupported } from '../utils/paymentSupportUtils';
import { isWebView, fromMobileApp } from '../../../../common/config/utils';
import { isIapAvailable } from '../utils/iapSupportUtils';
import { performAppleMerchantSetup } from '../setup';

// ============================================================================
// Setup Types (consumed by registry entries and useSetupAction)
// ============================================================================

export interface PerformSetupOptions {
  context: any;
  paymentSourceId: number;
  analytics: AnalyticsContextValue;
  log: OnLogEvent;
  onRefreshData?: () => Promise<void>;
}

// ============================================================================
// Types
// ============================================================================

/** Controls when a payment kind appears in option / add-button lists */
export type PaygVisibility = 'always' | 'paygOnly' | 'nonPaygOnly';

/**
 * Complete configuration for a single payment method kind.
 *
 * Groups together data that was previously spread across:
 * - `PAYMENT_METHOD_ADD_CONFIG` (constants.ts)
 * - `isPaymentMethodSupported` / `getPaymentMethodDisplayName` (paymentSupportUtils.ts)
 * - `getOptionIconKey` / `getOptionTextKey` / `getOptionButtonClass` / `getOptionIconClass` (PurchaseDetailsScreen.tsx)
 * - `getAddSourceIconId` / `getAddButtonClassName` (PaymentMethodsListScreen.tsx)
 * - Hardcoded `AddPaymentButtons` rendering (PaymentMethodsListScreen.tsx)
 */
export interface PaymentKindConfig {
  // --- Add behavior ---
  /** Whether this kind needs a dedicated setup screen (e.g., credit card form) */
  requiresSetupScreen: boolean;
  /** Whether adding this kind immediately starts processing (e.g., PayPal popup, Apple Merchant validation) */
  shouldStartProcessingDirectly?: boolean;
  /** Whether this kind opens a popup window for payment. Used to pre-open the popup synchronously in the click handler for Safari. */
  opensPopup?: boolean;

  // --- Device support ---
  /** Returns Promise if this payment method is supported. */
  isSupported: () => Promise<boolean>;

  // --- Display ---
  /** Human-readable name for logging and error messages */
  displayName: string;
  /**
   * Analytics event name for this payment kind (e.g., 'credit card', 'paypal', 'IAP').
   * Defaults to `displayName.toLowerCase()` when omitted — only set explicitly
   * when the analytics name differs from the display name.
   */
  analyticsName?: string;
  /** Rendering order in button lists (lower = higher priority) */
  renderOrder: number;

  // --- PAYG visibility ---
  /** Controls when this kind is visible: always, paygOnly, or nonPaygOnly */
  paygVisibility: PaygVisibility;

  // --- PurchaseDetailsScreen option button ---
  /** Icon key for the option button (null if using nativeButton) */
  optionIconKey: string | null;
  /** Text key for the option button label */
  optionTextKey: string;
  /** CSS modifier suffix for button class (e.g., 'PP' -> classes.paymentOptionButtonPP) */
  optionButtonCssModifier?: string;
  /** CSS modifier suffix for icon class (e.g., 'GP' -> classes.paymentOptionIconGP) */
  optionIconCssModifier?: string;

  // --- PaymentMethodsListScreen add button ---
  /** Icon key for the add button */
  addButtonIconKey: string;
  /** Text key for the add button label */
  addButtonTextKey: string;
  /** CSS modifier suffix for add button class */
  addButtonCssModifier?: string;
  /** CSS modifier suffix for add button icon class */
  addButtonIconCssModifier?: string;

  // --- Native button rendering ---
  /**
   * When set, renders a platform-native button (e.g. Apple Pay web component)
   * instead of a regular styled button.
   *
   * `kind` identifies the native button implementation ('apple-pay', etc.).
   * `type` is passed through to the native component (e.g. 'pay' | 'set-up').
   * `onClick` — optional factory called synchronously in the user's click handler
   *   to create a browser-restricted session (e.g. ApplePaySession).
   *   Returns the session object, or null on failure.
   *   When omitted, clicking the button proceeds without session creation.
   */
  nativeButton?: {
    kind: string;
    type: string;
    onClick?: (amount: number, context: any) => any | null;
  };

  /**
   * When true, this kind's charge processor prepares its payment up front and
   * owns the click→sheet (e.g. Google Pay via Checkout.com Flow). The purchase
   * screen gates its Pay button on the processor's `onReady`, and the user's
   * click is routed through the processor's synchronous trigger (see
   * `nativeTriggerRef`) instead of the generic startSignal path — so the GP
   * sheet opens on the first click, within the browser's gesture window.
   */
  rendersOwnPayButton?: boolean;

  // --- Setup ---
  /**
   * When set, this kind requires an interactive setup flow (e.g. Apple Merchant
   * token enrollment) before it can be used for payment.
   *
   * The function receives context + dependencies and returns a Promise that
   * resolves on success or rejects on error/cancel. The session is created
   * synchronously inside the Promise constructor so it stays within the
   * browser's trusted-event window.
   *
   * Screens use `useSetupAction` to call this and manage loading/error state.
   */
  performSetup?: (options: PerformSetupOptions) => Promise<void>;
}

// ============================================================================
// Registry
// ============================================================================

const alwaysSupported = (): Promise<boolean> => Promise.resolve(true);

/**
 * Create an ApplePaySession synchronously from a user gesture handler.
 * Called by the nativeButton.onClick registry hook so the session is created
 * within the browser's trusted-event window.
 */
const createApplePaySession = (amount: number, context: any): any | null => {
  if (typeof window === 'undefined' || !window.ApplePaySession) return null;
  const { merchant_id: merchantId, ...config } = context || {};
  if (!merchantId || !config.total) return null;
  config.total.amount = String(amount);
  try {
    return new (window as any).ApplePaySession(6, config);
  } catch {
    return null;
  }
};

/**
 * Payment Kind Registry — single source of truth for all payment method kinds.
 *
 * Order of entries matches `renderOrder` for readability.
 */
export const PAYMENT_KIND_REGISTRY: Record<PaymentMethodKind, PaymentKindConfig> = {
  gp: {
    requiresSetupScreen: false,
    isSupported: googlePayIsSupported,
    displayName: 'Google Pay',
    renderOrder: 1,
    paygVisibility: 'always',
    // Checkout.com Flow prepares the session + button up front and owns the
    // click→sheet, so the click is routed through the processor (MBW-2436).
    rendersOwnPayButton: true,
    optionIconKey: PURCHASE_FLOW_ICONS.ADD_GOOGLE_PAY,
    optionTextKey: PURCHASE_FLOW_TEXT_KEYS.OPTION_CONTINUE_GOOGLE_PAY,
    optionButtonCssModifier: 'GP',
    optionIconCssModifier: 'GP',
    addButtonIconKey: PURCHASE_FLOW_ICONS.ADD_GOOGLE_PAY,
    addButtonTextKey: PURCHASE_FLOW_TEXT_KEYS.OPTION_CONTINUE_GOOGLE_PAY,
    addButtonCssModifier: 'GP',
    addButtonIconCssModifier: 'GP'
  },
  ap: {
    requiresSetupScreen: false,
    isSupported: applePayIsSupported,
    displayName: 'Apple Pay',
    renderOrder: 2,
    paygVisibility: 'nonPaygOnly',
    optionIconKey: null,
    optionTextKey: PURCHASE_FLOW_TEXT_KEYS.OPTION_CONTINUE_APPLE_PAY,
    addButtonIconKey: PURCHASE_FLOW_ICONS.ADD_APPLE_PAY,
    addButtonTextKey: PURCHASE_FLOW_TEXT_KEYS.OPTION_CONTINUE_APPLE_PAY,
    nativeButton: {
      kind: 'apple-pay',
      type: 'pay',
      onClick: createApplePaySession
    }
  },
  am: {
    requiresSetupScreen: false,
    shouldStartProcessingDirectly: true,
    isSupported: applePayIsSupported,
    displayName: 'Apple Pay',
    analyticsName: 'apple merchant token',
    renderOrder: 3,
    paygVisibility: 'paygOnly',
    optionIconKey: null,
    optionTextKey: PURCHASE_FLOW_TEXT_KEYS.OPTION_SETUP_APPLE_PAY,
    addButtonIconKey: PURCHASE_FLOW_ICONS.ADD_APPLE_PAY,
    addButtonTextKey: PURCHASE_FLOW_TEXT_KEYS.OPTION_SETUP_APPLE_PAY,
    nativeButton: { kind: 'apple-pay', type: 'set-up' },
    performSetup: performAppleMerchantSetup
  },
  cc: {
    requiresSetupScreen: true,
    isSupported: alwaysSupported,
    displayName: 'Credit Card',
    renderOrder: 4,
    paygVisibility: 'always',
    optionIconKey: PURCHASE_FLOW_ICONS.ADD_CREDIT_CARD,
    optionTextKey: PURCHASE_FLOW_TEXT_KEYS.OPTION_ADD_CARD,
    addButtonIconKey: PURCHASE_FLOW_ICONS.ADD_CREDIT_CARD,
    addButtonTextKey: PURCHASE_FLOW_TEXT_KEYS.OPTION_ADD_CARD
  },
  pp: {
    requiresSetupScreen: false,
    shouldStartProcessingDirectly: true,
    opensPopup: true,
    isSupported: (): Promise<boolean> => Promise.resolve(!isWebView() || fromMobileApp),
    displayName: 'PayPal',
    renderOrder: 5,
    paygVisibility: 'nonPaygOnly',
    optionIconKey: PURCHASE_FLOW_ICONS.ADD_PAYPAL,
    optionTextKey: PURCHASE_FLOW_TEXT_KEYS.OPTION_CONTINUE_PAYPAL,
    optionButtonCssModifier: 'PP',
    addButtonIconKey: PURCHASE_FLOW_ICONS.ADD_PAYPAL,
    addButtonTextKey: PURCHASE_FLOW_TEXT_KEYS.OPTION_CONTINUE_PAYPAL,
    addButtonCssModifier: 'PP'
  },
  iap: {
    requiresSetupScreen: false,
    isSupported: (): Promise<boolean> => Promise.resolve(isIapAvailable()),
    displayName: 'In-app purchase',
    analyticsName: 'IAP',
    renderOrder: 6,
    paygVisibility: 'always',
    optionIconKey: PURCHASE_FLOW_ICONS.IAP,
    optionTextKey: PURCHASE_FLOW_TEXT_KEYS.OPTION_IN_APP_PURCHASE,
    addButtonIconKey: PURCHASE_FLOW_ICONS.ADD_IAP,
    addButtonTextKey: PURCHASE_FLOW_TEXT_KEYS.OPTION_IN_APP_PURCHASE
  }
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Look up the full config for a payment method kind.
 * Returns undefined for unknown kinds.
 */
export const getKindConfig = (kind: string): PaymentKindConfig | undefined => PAYMENT_KIND_REGISTRY[kind as PaymentMethodKind];

/**
 * Check if a payment method is supported on the current device.
 * Returns a promise when the kind uses async support check (e.g. Apple Pay).
 */
export const isPaymentMethodSupported = async (kind: string): Promise<boolean> => await getKindConfig(kind)?.isSupported() ?? true;

/**
 * Get display name for a payment method kind (for logging / error messages).
 */
export const getPaymentMethodDisplayName = (kind: string): string => getKindConfig(kind)?.displayName ?? 'Payment method';

/**
 * Get analytics name for a payment method kind.
 * Returns `analyticsName` if set, otherwise falls back to `displayName.toLowerCase()`.
 * This means most new gateways only need `displayName` — the analytics name is derived.
 */
export const getPaymentMethodAnalyticsName = (kind: string): string | undefined => {
  const config = getKindConfig(kind);
  if (!config) return undefined;
  return config.analyticsName ?? config.displayName.toLowerCase();
};

/**
 * Get the text key for an unsupported payment method message.
 * Returns null if the method IS supported.
 */
export const getUnsupportedPaymentMessage = async (kind: string): Promise<string | null> => {
  const supported = await isPaymentMethodSupported(kind);
  return supported ? null : PURCHASE_FLOW_TEXT_KEYS.PAYMENT_NOT_SUPPORTED_MESSAGE;
};

/**
 * Get the add-behavior config for a payment method kind.
 * Used by `usePurchaseFlowHandlers` to decide whether to navigate to a setup screen
 * or add directly via API.
 */
export const getAddBehavior = (kind: string): Pick<PaymentKindConfig, 'requiresSetupScreen' | 'shouldStartProcessingDirectly'> => getKindConfig(kind) ?? { requiresSetupScreen: false };

/**
 * Returns all payment method kinds that should be visible given the current PAYG mode,
 * filtered by device support and sorted by render order.
 */
export const getVisibleKinds = async (isPayg: boolean): Promise<PaymentMethodKind[]> => {
  const entries = (Object.entries(PAYMENT_KIND_REGISTRY) as [PaymentMethodKind, PaymentKindConfig][])
    .filter(([, config]) => {
      if (config.paygVisibility === 'paygOnly') return isPayg;
      if (config.paygVisibility === 'nonPaygOnly') return !isPayg;
      return true;
    });
  const results = await Promise.all(entries.map(async ([kind, config]) => {
    const isSupported = await config.isSupported();
    return isSupported ? kind : null;
  }));
  return results
    .filter((kind): kind is PaymentMethodKind => kind !== null)
    .sort((a, b) => (getKindConfig(a)?.renderOrder ?? 0) - (getKindConfig(b)?.renderOrder ?? 0));
};
